import {
  Check,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";
import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> {
  label: string;
  children: ReactNode;
  icon?: LucideIcon;
  triggerClassName?: string;
}

function optionText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(optionText).join("");
  return "";
}

function readOptions(children: ReactNode): SelectOption[] {
  return Children.toArray(children)
    .filter(isValidElement)
    .map((child) => {
      const props = child.props as {
        value?: string | number;
        children?: ReactNode;
        disabled?: boolean;
      };
      const label = optionText(props.children);
      return {
        value: props.value === undefined ? label : String(props.value),
        label,
        disabled: props.disabled,
      };
    });
}

export function CustomSelect({
  label,
  children,
  icon: Icon,
  className = "",
  triggerClassName = "",
  value,
  defaultValue,
  onChange,
  disabled,
  name,
  id,
}: CustomSelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const listboxId = `${selectId}-listbox`;
  const options = useMemo(() => readOptions(children), [children]);
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(
    defaultValue === undefined ? options[0]?.value ?? "" : String(defaultValue),
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const currentValue = value === undefined ? internalValue : String(value);
  const selected =
    options.find((option) => option.value === currentValue) ?? options[0];

  useEffect(() => {
    function closeOnOutside(event: MouseEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    }

    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, []);

  function selectValue(nextValue: string) {
    const option = options.find((item) => item.value === nextValue);
    if (!option || option.disabled) return;
    if (value === undefined) setInternalValue(nextValue);
    setOpen(false);
    onChange?.({
      target: { value: nextValue, name },
      currentTarget: { value: nextValue, name },
    } as ChangeEvent<HTMLSelectElement>);
  }

  function moveSelection(direction: 1 | -1) {
    const available = options.filter((option) => !option.disabled);
    const currentIndex = available.findIndex(
      (option) => option.value === currentValue,
    );
    const next =
      available[
        (currentIndex + direction + available.length) % available.length
      ];
    if (next) selectValue(next.value);
  }

  return (
    <div
      ref={wrapperRef}
      className={`custom-select ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""} ${className}`.trim()}
      data-native="false"
    >
      <button
        type="button"
        id={selectId}
        className={`custom-select__trigger ${triggerClassName}`.trim()}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={label}
        disabled={disabled}
        onClick={() => setOpen((state) => !state)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
          if (event.key === "ArrowDown") {
            event.preventDefault();
            if (!open) setOpen(true);
            else moveSelection(1);
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            if (!open) setOpen(true);
            else moveSelection(-1);
          }
        }}
      >
        {Icon && <Icon size={17} aria-hidden="true" />}
        <span>{selected?.label ?? label}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>

      {open && (
        <div
          id={listboxId}
          className="custom-select__menu"
          role="listbox"
          aria-labelledby={selectId}
        >
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              role="option"
              className={`custom-select__option ${
                option.value === currentValue ? "is-selected" : ""
              }`.trim()}
              aria-selected={option.value === currentValue}
              disabled={option.disabled}
              onClick={() => selectValue(option.value)}
            >
              <span>{option.label}</span>
              {option.value === currentValue && <Check size={15} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
